/**
 * <s-page-loader> — full-screen Black Line logo shown while a page loads, instead of a blank screen.
 *
 * - Visible from first paint (markup + CSS in layout/theme.liquid / config/styles.css).
 * - Hides once the page has loaded, with a short minimum so the logo never just flickers,
 *   and a hard cap so a slow asset never holds the page hostage.
 * - Shows again when a shopper clicks a link to another page on the store, covering the
 *   browser's blank gap until the next page's loader takes over.
 * - Back/forward cache restores hide it immediately.
 * A CSS fallback animation hides it after a few seconds even if this script never runs.
 */
const MIN_VISIBLE_MS = 450;
const MAX_VISIBLE_MS = 2200;

class SPageLoader extends HTMLElement {
  #ac;
  #start = performance.now();

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    if (document.readyState === 'complete') {
      this.#hideSoon();
    } else {
      window.addEventListener('load', () => this.#hideSoon(), { ...opts, once: true });
      setTimeout(() => this.#hide(), MAX_VISIBLE_MS);
    }

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.#hide();
    }, opts);

    document.addEventListener('click', (event) => this.#onClick(event), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #hideSoon() {
    const elapsed = performance.now() - this.#start;
    setTimeout(() => this.#hide(), Math.max(0, MIN_VISIBLE_MS - elapsed));
  }

  #hide() {
    this.setAttribute('data-done', '');
  }

  #onClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || link.target && link.target !== '_self' || link.hasAttribute('download')) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;
    if (/^\/(cart|checkout|account)/.test(url.pathname) && link.closest('dialog')) return;

    this.removeAttribute('data-done');
  }
}

if (!customElements.get('s-page-loader')) {
  customElements.define('s-page-loader', SPageLoader);
}
